import React, { useEffect, useState } from 'react';
import useAppStore from '../../store/useAppStore.js';
import ConfirmDialog from '../common/ConfirmDialog.js';
import UserDialog from '../common/dialogs/UserDialog.js';
import UserHeader from './UserHeader.js';
import UserTable from './UserTable.js';

const AdminUsers = () => {
  const {
    admin,
    loadUsers,
    createUser,
    updateUser,
    toggleUserActive,
    deleteUser
  } = useAppStore((state) => ({
    admin: state.admin,
    loadUsers: state.admin.loadUsers,
    createUser: state.admin.createUser,
    updateUser: state.admin.updateUser,
    toggleUserActive: state.admin.toggleUserActive,
    deleteUser: state.admin.deleteUser
  }));

  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ 
    isOpen: false, 
    userId: null, 
    type: null,
    title: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!admin.usersLoaded && !admin.usersLoading) {
      loadUsers();
    }
  }, [admin.usersLoaded, admin.usersLoading, loadUsers]);

  const handleCreateUser = () => {
    setSelectedUser(null);
    setShowUserDialog(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowUserDialog(true);
  };

  const handleSaveUser = async (userData) => {
    setLoading(true);
    try {
      if (selectedUser) {
        await updateUser(selectedUser.id, userData);
      } else {
        await createUser(userData);
      }
      setShowUserDialog(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Failed to save user:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = (userId) => {
    setConfirmDialog({
      isOpen: true,
      userId,
      type: 'toggle',
      title: 'Confirm status change',
      message: "Do you want to toggle this user's active status?"
    });
  };

  const handleDelete = (userId) => {
    setConfirmDialog({
      isOpen: true,
      userId,
      type: 'delete',
      title: 'Delete User',
      message: 'Are you sure you want to delete this user? This action cannot be undone.'
    });
  };

  const handleConfirmAction = async () => {
    try {
      if (confirmDialog.type === 'delete') {
        await deleteUser(confirmDialog.userId);
      } else {
        await toggleUserActive(confirmDialog.userId);
      }
    } catch (error) {
      console.error('Failed to perform user action:', error);
    } finally {
      setConfirmDialog({ isOpen: false, userId: null, type: null, title: '', message: '' });
    }
  };

  return (
    <div className="space-y-6">
      <UserHeader />
      
      <UserTable 
        onCreateUser={handleCreateUser}
        onEditUser={handleEditUser}
        onDeactivateUser={handleDeactivate}
        onDeleteUser={handleDelete}
      />

      <UserDialog
        isOpen={showUserDialog}
        onClose={() => setShowUserDialog(false)}
        onSave={handleSaveUser}
        user={selectedUser}
        branches={admin.branches}
        loading={loading}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, userId: null, type: null, title: '', message: '' })}
        onConfirm={handleConfirmAction}
        title={confirmDialog.title}
        message={confirmDialog.message}
      />
    </div>
  );
};

export default AdminUsers;
